import _ from 'lodash';

export class StoreManager {
  constructor() {
    this._store = new Map();
    this._prefix = new Map();
    this._listeners = new Map();
    this._watchers = new Map();
  }

  emit(key, value) {
    _(this._listeners.get(key)).forEach(fn => fn(value));
    _(this._watchers.get(key)).forEach(({ prop, cb, deep }) => {
      const val = deep ? _.get(value, prop) : _.get(value, prop, undefined);
      if (val !== undefined) cb(val);
    });
  }

  watch(key, prop, callback) {
    if (!this._watchers.has(key)) this._watchers.set(key, []);
    const item = { prop, cb: callback, deep: false };
    this._watchers.get(key).push(item);
    return () => this._watchers.set(key, this._watchers.get(key).filter(x => x !== item));
  }

  watchDeep(key, path, callback) {
    if (!this._watchers.has(key)) this._watchers.set(key, []);
    const item = { prop: path, cb: callback, deep: true };
    this._watchers.get(key).push(item);
    return () => this._watchers.set(key, this._watchers.get(key).filter(x => x !== item));
  }

  listen(key, callback) {
    if (!this._listeners.has(key)) this._listeners.set(key, []);
    this._listeners.get(key).push(callback);
  }

  unlisten(key, callback) {
    const listeners = this._listeners.get(key);
    if (listeners) this._listeners.set(key, listeners.filter(fn => fn !== callback));
  }

  save(key, value) {
    this._store.set(key, value);
    this.emit(key, value);
    return this;
  }

  get(key) {
    if (!key) return Object.fromEntries(this._store);
    if (key.includes('.')) {
      const [root, ...path] = key.split('.');
      return path.reduce((acc, part) => _.get(acc, part), this._store.get(root));
    }
    return this._store.get(key);
  }

  update(table, path, value) {
    const storeKey = this._prefix.get(table) || table;
    const store = this._store.get(storeKey);
    if (store) {
      _.set(store, path, value);
      this.emit(storeKey, store);
    }
    return this;
  }

  invalidate(key) {
    this._store.delete(key);
    return this;
  }

  setPrefix(tableName, prefix) {
    this._prefix.set(tableName, prefix);
    return this;
  }

  getPrefix(tableName) {
    return this._prefix.get(tableName) || tableName;
  }

  getTableStore(table) {
    const tableKey = _.isString(table) ? table : table.name;
    const prefix = this.getPrefix(tableKey);
    return {
      get: (key) => {
        const storeBase = this._store.get(prefix);
        return key ? key.split('.').reduce((acc, part) => _.get(acc, part), storeBase) : storeBase;
      }
    };
  }

  createStoreObject(storeKey, initialData = null, initialError = null) {
    const activeWatchers = new Set();
    const existingStore = this._store.get(storeKey);

    const storeObj = {
      data: _.get(existingStore, 'data', initialData),
      loading: _.has(existingStore, 'loading') ? _.get(existingStore, 'loading') : !initialData,
      error: _.get(existingStore, 'error', initialError),
      
      watch: (prop, cb) => {
        const unwatch = this.watch(storeKey, prop, cb);
        activeWatchers.add(unwatch);
        return () => { unwatch(); activeWatchers.delete(unwatch); };
      },
      
      watchDeep: (path, cb) => {
        const unwatch = this.watchDeep(storeKey, path, cb);
        activeWatchers.add(unwatch);
        return () => { unwatch(); activeWatchers.delete(unwatch); };
      },
      
      subscribe: (callback) => {
        const listener = (storeData) => _.isFunction(callback) && callback(storeData);
        this.listen(storeKey, listener);
        return () => this.unlisten(storeKey, listener);
      },
      
      unwatch: (unwatchFn) => _.isFunction(unwatchFn) && unwatchFn(),
      
      unwatchAll: () => {
        activeWatchers.forEach(unwatch => unwatch());
        activeWatchers.clear();
      },
      
      destroy: () => {
        activeWatchers.forEach(unwatch => unwatch());
        activeWatchers.clear();
        this._store.delete(storeKey);
        this._listeners.delete(storeKey);
        this._watchers.delete(storeKey);
      }
    };

    return { storeObj, activeWatchers };
  }

  getAll(pattern = null) {
    return _(this._store)
      .pickBy((value, key) => !pattern || this._matchesPattern(key, pattern))
      .fromPairs()
      .value();
  }

  getKeys(pattern = null) {
    return _(this._store.keys())
      .filter(key => !pattern || this._matchesPattern(key, pattern))
      .value();
  }

  getValues(pattern = null) {
    return _(this._store)
      .pickBy((value, key) => !pattern || this._matchesPattern(key, pattern))
      .values()
      .value();
  }

  _matchesPattern(key, pattern) {
    const regexPattern = pattern.replace(/\*/g, '.*').replace(/\?/g, '.');
    return new RegExp(`^${regexPattern}$`, 'i').test(key);
  }

  clone() {
    const cloned = new StoreManager();
    cloned._store = new Map(this._store);
    cloned._prefix = new Map(this._prefix);
    return cloned;
  }
}



// WEBPACK FOOTER //
// ./src/app/lib/directus-sdk/store.js